// ==============================|| THEME CONSTANT ||============================== //

/** Google Maps/Places API key for address picker (restrict by referrer in Google Cloud Console) */
export const GOOGLE_MAPS_API_KEY = 'AIzaSyDpQaOmPkUPhwD9Aoq0VwGELredG0pxANE';

export const twitterColor = '#1DA1F2';
export const facebookColor = '#3b5998';
export const linkedInColor = '#0e76a8';

// App-wide colors
export const searchHighlightColor = '#FFEB3B'; // Yellow highlight for search results

export const APP_DEFAULT_PATH = '/';
export const HORIZONTAL_MAX_ITEM = 7;
export const DRAWER_WIDTH = 240;
export const MINI_DRAWER_WIDTH = 60;

export const CSS_VAR_PREFIX = '';

export let SimpleLayoutType;

(function (SimpleLayoutType) {
  SimpleLayoutType['SIMPLE'] = 'simple';
  SimpleLayoutType['LANDING'] = 'landing';
})(SimpleLayoutType || (SimpleLayoutType = {}));

export let ThemeMode;

(function (ThemeMode) {
  ThemeMode['LIGHT'] = 'light';
  ThemeMode['DARK'] = 'dark';
  ThemeMode['SYSTEM'] = 'system';
})(ThemeMode || (ThemeMode = {}));

export const DEFAULT_THEME_MODE = ThemeMode.LIGHT;

export let MenuOrientation;

(function (MenuOrientation) {
  MenuOrientation['VERTICAL'] = 'vertical';
  MenuOrientation['HORIZONTAL'] = 'horizontal';
  MenuOrientation['MINI_VERTICAL'] = 'mini-vertical';
})(MenuOrientation || (MenuOrientation = {}));

export let ThemeDirection;

(function (ThemeDirection) {
  ThemeDirection['LTR'] = 'ltr';
  ThemeDirection['RTL'] = 'rtl';
})(ThemeDirection || (ThemeDirection = {}));

export let NavActionType;

(function (NavActionType) {
  NavActionType['FUNCTION'] = 'function';
  NavActionType['LINK'] = 'link';
})(NavActionType || (NavActionType = {}));

export let Gender;

(function (Gender) {
  Gender['MALE'] = 'Male';
  Gender['FEMALE'] = 'Female';
})(Gender || (Gender = {}));

export let DropzoneType;

(function (DropzoneType) {
  DropzoneType['DEFAULT'] = 'default';
  DropzoneType['STANDARD'] = 'standard';
})(DropzoneType || (DropzoneType = {}));

export let AuthProvider;

(function (AuthProvider) {
  AuthProvider['JWT'] = 'jwt';
  AuthProvider['FIREBASE'] = 'firebase';
  AuthProvider['AUTH0'] = 'auth0';
  AuthProvider['AWS'] = 'aws';
  AuthProvider['SUPABASE'] = 'supabase';
})(AuthProvider || (AuthProvider = {}));

export const APP_AUTH = AuthProvider.JWT;

// ==============================|| BUSINESS CONSTANTS ||============================== //

/**
 * CLAIM TYPES
 * 
 * Hardcoded business constants that define the types of insurance claims
 * in the dental benefits system. These values are stable and won't change
 * for 10-20 years, making them perfect candidates for frontend constants.
 * 
 * These match the backend config.php $CONFIG['app']['claimTypes'] array
 * and are used throughout the application for:
 * - Displaying claim type names in UI
 * - Mapping claim_type_id to human-readable names
 * - Form validation and dropdown options
 * 
 * @constant {Object} CLAIM_TYPES
 * @property {string} 1 - PEF Reporting (Provincial Emergency Fund)
 * @property {string} 2 - SPSP Payment (Special Program Support Payment)
 * @property {string} 3 - GPSP Payment (General Program Support Payment)
 * @property {string} 4 - EMERG Payment (Emergency payment)
 * @property {string} 5 - PPO Payment (Preferred Provider Organization)
 * @property {string} 6 - USP Payment (Unspecified Support Payment)
 * 
 * @example
 * // Get claim type name by ID
 * const claimTypeName = CLAIM_TYPES[1]; // "PEF Reporting"
 * 
 * // Use in dropdown options
 * const options = Object.entries(CLAIM_TYPES).map(([id, name]) => ({
 *   value: id,
 *   label: name
 * }));
 */
export const CLAIM_TYPES = {
  1: 'PEF Reporting',     // Provincial Emergency Fund - reporting claims
  2: 'SPSP Payment',      // Special Program Support Payment - direct payments
  3: 'GPSP Payment',      // General Program Support Payment - standard payments
  4: 'EMERG Payment',     // Emergency payment - urgent/emergency claims
  5: 'PPO Payment',       // Preferred Provider Organization - network payments
  6: 'USP Payment'        // Unspecified Support Payment - miscellaneous payments
};

/**
 * SERVICE CATEGORIES
 * 
 * Hardcoded business constants that define the categories of dental services
 * in the dental benefits system. These values are stable and won't change
 * for 10-20 years, making them perfect candidates for frontend constants.
 * 
 * These match the backend config.php $CONFIG['app']['categoryOfService'] array
 * and are used throughout the application for:
 * - Displaying service category names in UI
 * - Mapping category_of_service_code to human-readable names
 * - Form validation and dropdown options
 * - Coverage table displays
 * 
 * @constant {Object} SERVICE_CATEGORIES
 * @property {Object} '00' - Diagnostic services
 * @property {Object} '10' - Preventive services
 * @property {Object} '20' - Minor Restorative services
 * @property {Object} '24' - Major Restorative services
 * @property {Object} '30' - Endodontics services
 * @property {Object} '40' - Periodontics services
 * @property {Object} '50' - Prosthetics - Removable services
 * @property {Object} '60' - Prosthodontics - Fixed services
 * @property {Object} '70' - Oral Surgery services
 * @property {Object} '80' - Orthodontics services
 * @property {Object} '90' - Adjunctive General Services
 * 
 * @example
 * // Get service category name by code
 * const categoryName = SERVICE_CATEGORIES['00'].Category; // "Diagnostic"
 * 
 * // Get full category info
 * const categoryInfo = SERVICE_CATEGORIES['10']; // { Category: "Preventive", Description: "..." }
 * 
 * // Use in dropdown options
 * const options = Object.entries(SERVICE_CATEGORIES).map(([code, info]) => ({
 *   value: code,
 *   label: info.Category
 * }));
 */
export const SERVICE_CATEGORIES = {
  '00': {
    Category: 'Diagnostic',
    Description: 'Exams, evaluations, and X-rays to diagnose oral health status'
  },
  '10': {
    Category: 'Preventive',
    Description: 'Services to prevent dental disease like cleanings and fluoride treatments'
  },
  '20': {
    Category: 'Minor Restorative',
    Description: 'Small fillings and repairs'
  },
  '24': {
    Category: 'Major Restorative',
    Description: 'Large or complex restorative treatments such as inlays, onlays, or extensive fillings'
  },
  '30': {
    Category: 'Endodontics',
    Description: 'Root canal therapy and dental pulp treatments'
  },
  '40': {
    Category: 'Periodontics',
    Description: 'Treatments for gum diseases and supporting structures'
  },
  '50': {
    Category: 'Prosthetics - Removable',
    Description: 'Removable dental appliances such as dentures and partials'
  },
  '60': {
    Category: 'Prosthodontics - Fixed',
    Description: 'Fixed dental prostheses like crowns and bridges'
  },
  '70': {
    Category: 'Oral Surgery',
    Description: 'Surgical procedures involving teeth, gums, and jaw'
  },
  '80': {
    Category: 'Orthodontics',
    Description: 'Treatments for correcting teeth and jaw alignment (e.g., braces)'
  },
  '90': {
    Category: 'Adjunctive General Services',
    Description: 'Supportive care like emergency treatment, consultations, and palliative care'
  }
};

/**
 * FEE GUIDE TYPES
 * 
 * Hardcoded business constants that define the types of fee guides
 * in the dental benefits system. These values are stable and won't change
 * for 10-20 years, making them perfect candidates for frontend constants.
 * 
 * These match the backend config.php $CONFIG['app']['feeGuideType'] array
 * and are used throughout the application for:
 * - Displaying fee guide type names in UI
 * - Mapping type (integer) to human-readable names
 * - Form validation and dropdown options
 * - Excluded procedures type display
 * 
 * @constant {Object} FEE_GUIDE_TYPES
 * @property {string} 1 - GP (General Practice)
 * @property {string} 2 - PERIO (Periodontics)
 * @property {string} 3 - PAEDO (Pediatric Dentistry)
 * @property {string} 4 - ENDO (Endodontics)
 * @property {string} 5 - O.S. (Oral Surgery)
 * @property {string} 6 - DENT (Dentistry)
 * @property {string} 7 - ORTHO (Orthodontics)
 * 
 * @example
 * // Get fee guide type name by ID
 * const typeName = FEE_GUIDE_TYPES[1]; // "GP"
 * 
 * // Use in dropdown options
 * const options = Object.entries(FEE_GUIDE_TYPES).map(([id, name]) => ({
 *   value: id,
 *   label: name
 * }));
 */
export const FEE_GUIDE_TYPES = {
  1: 'GP',
  2: 'PERIO',
  3: 'PAEDO',
  4: 'ENDO',
  5: 'O.S.',
  6: 'DENT',
  7: 'ORTHO'
};

/**
 * Fee guide row: tooth number field behavior (`fee_guides.tooth_num`).
 * Binary only: N/A vs optional. Mandatory enforcement lives in claim-entry validation, not this flag.
 */
export const FEE_GUIDE_TOOTH_NUM = {
  0: 'N/A (hide)',
  1: 'Optional'
};

// ==============================|| THEME CONFIG ||============================== //

const config = {
  fontFamily: `'Inter', sans-serif`,
  fontSize: 16,
  menuOrientation: MenuOrientation.VERTICAL,
  miniDrawer: false, // Custom: kept for backward compatibility
  container: false,
  // Prod default (theme7 green). Test is forced to theme5; Dev (localhost / ffdp3.loc) is forced to default blue.
  presetColor: 'theme7',
  themeDirection: ThemeDirection.LTR
  // Note: mode is handled by ThemeProvider (useColorScheme hook), not stored in config
};

export default config;
